import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { auth, importLogs as importLogsApi, users as usersApi } from '../api';
import {
  FiFileText, FiDatabase, FiFolder, FiRefreshCw,
  FiChevronDown, FiChevronUp, FiGitCommit, FiLogIn, FiDownload,
} from 'react-icons/fi';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const TABS = [
  { id: 'sessions', label: 'Входы', icon: FiLogIn },
  { id: 'db', label: 'Импорты в БД', icon: FiDatabase },
  { id: 'compare', label: 'Сравнения 1С', icon: FiGitCommit },
  { id: 'reports', label: 'Отчёты sync', icon: FiFolder },
  { id: 'files', label: 'Файловые логи', icon: FiFileText },
];

const CHANGE_LABELS = {
  new: { label: 'Новая позиция', color: 'bg-green-100 text-green-800' },
  qty_increase: { label: 'Поступление', color: 'bg-blue-100 text-blue-800' },
  qty_decrease: { label: 'Списание', color: 'bg-orange-100 text-orange-800' },
  sold_missing: { label: 'Пропала из выгрузки', color: 'bg-red-100 text-red-800' },
  revived: { label: 'Вернулась на склад', color: 'bg-purple-100 text-purple-800' },
  recoded: { label: 'Сменили код 1С', color: 'bg-amber-100 text-amber-800' },
};

function statusBadge(status) {
  const map = {
    success: 'bg-green-100 text-green-800',
    partial: 'bg-yellow-100 text-yellow-800',
    failed: 'bg-red-100 text-red-800',
    confirmed: 'bg-green-100 text-green-800',
    pending: 'bg-blue-100 text-blue-800',
  };
  const cls = map[status] || 'bg-gray-100 text-gray-800';
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>
      {status || '—'}
    </span>
  );
}

function changeTypeBadge(type) {
  const cfg = CHANGE_LABELS[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return format(new Date(value), 'dd.MM.yyyy HH:mm', { locale: ru });
  } catch {
    return value;
  }
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function basename(path) {
  if (!path) return '—';
  const parts = path.split(/[/\\]/);
  return parts[parts.length - 1] || path;
}

function qtyCell(oldQ, newQ) {
  const oldVal = oldQ ?? '—';
  const newVal = newQ ?? '—';
  if (oldVal === newVal) return newVal;
  return `${oldVal} → ${newVal}`;
}

function ChangesTable({ items, emptyText }) {
  if (!items?.length) {
    return <div className="text-sm text-gray-500 py-4">{emptyText}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Тип</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">КОД 1С</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Вендор</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Наименование</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Кол-во</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Δ</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {items.map((row, idx) => (
            <tr key={row.id || `${row.code_1c}-${idx}`} className="hover:bg-gray-50">
              <td className="px-3 py-2">{changeTypeBadge(row.change_type)}</td>
              <td className="px-3 py-2 font-mono text-xs">{row.code_1c}</td>
              <td className="px-3 py-2 text-gray-600">{row.vendor || '—'}</td>
              <td className="px-3 py-2 text-gray-800 max-w-xs truncate" title={row.name}>{row.name || '—'}</td>
              <td className="px-3 py-2 whitespace-nowrap">{qtyCell(row.old_quantity, row.new_quantity)}</td>
              <td className="px-3 py-2 whitespace-nowrap font-medium">
                {row.delta > 0 ? `+${row.delta}` : row.delta ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CompareChangesTable({ report }) {
  if (!report) return null;
  const updates = (report.updates || []).map((u) => ({
    change_type: u.was_sold ? 'revived' : (u.delta < 0 ? 'qty_decrease' : 'qty_increase'),
    code_1c: u.code_1c,
    name: u.name,
    vendor: u.vendor,
    old_quantity: u.old_quantity,
    new_quantity: u.new_quantity,
    delta: u.delta,
  }));
  const newItems = (report.new_items || []).map((u) => ({
    change_type: 'new',
    code_1c: u.code_1c,
    name: u.name,
    vendor: u.vendor,
    old_quantity: 0,
    new_quantity: u.quantity,
    delta: u.quantity,
  }));
  const soldMissing = (report.sold_missing || []).map((u) => ({
    change_type: 'sold_missing',
    code_1c: u.code_1c,
    name: u.name,
    vendor: u.vendor,
    old_quantity: u.quantity,
    new_quantity: 0,
    delta: -(u.quantity || 0),
  }));
  const all = [...newItems, ...updates, ...soldMissing];
  return (
    <ChangesTable
      items={all}
      emptyText="Отличий нет — выгрузка полностью совпадает с системой"
    />
  );
}

function Logs() {
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('sessions');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dataImportAvailable, setDataImportAvailable] = useState(false);

  const [dbLogs, setDbLogs] = useState([]);
  const [compareReports, setCompareReports] = useState([]);
  const [syncReports, setSyncReports] = useState([]);
  const [fileLogs, setFileLogs] = useState([]);
  const [sessionRows, setSessionRows] = useState([]);
  const [sessionStats, setSessionStats] = useState(null);
  const [sessionUsers, setSessionUsers] = useState([]);
  const [sessionFrom, setSessionFrom] = useState('');
  const [sessionTo, setSessionTo] = useState('');
  const [sessionUserId, setSessionUserId] = useState('');
  const [sessionBookedOnly, setSessionBookedOnly] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [expandedLogId, setExpandedLogId] = useState(null);
  const [logChanges, setLogChanges] = useState(null);
  const [expandedCompareId, setExpandedCompareId] = useState(null);
  const [compareDetail, setCompareDetail] = useState(null);
  const [expandedReport, setExpandedReport] = useState(null);
  const [reportDetail, setReportDetail] = useState(null);
  const [selectedFileLog, setSelectedFileLog] = useState(null);
  const [fileLogContent, setFileLogContent] = useState(null);

  useEffect(() => {
    loadPage();
  }, []);

  const loadPage = async () => {
    setLoading(true);
    setError('');
    try {
      const me = await auth.getCurrentUser();
      setCurrentUser(me);
      if (!me.is_admin) {
        setLoading(false);
        return;
      }
      await loadTabData('sessions', me);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  };

  const loadTabData = async (tab, user = currentUser) => {
    if (!user?.is_admin) return;
    setError('');
    try {
      if (tab === 'sessions') {
        const params = {
          date_from: sessionFrom || undefined,
          date_to: sessionTo || undefined,
          user_id: sessionUserId || undefined,
          booked_only: sessionBookedOnly || undefined,
        };
        const [data, users] = await Promise.all([
          importLogsApi.getSessions(params),
          sessionUsers.length ? Promise.resolve(sessionUsers) : usersApi.getAll(),
        ]);
        setSessionRows(data.items || []);
        setSessionStats(data.stats || null);
        if (typeof data.data_import_available === 'boolean') {
          setDataImportAvailable(data.data_import_available);
        }
        if (!sessionUsers.length) setSessionUsers(users || []);
      } else if (tab === 'db') {
        const data = await importLogsApi.getDbLogs();
        setDbLogs(data.items || []);
        setDataImportAvailable(data.data_import_available);
      } else if (tab === 'compare') {
        const data = await importLogsApi.getCompareReports();
        setCompareReports(data.items || []);
        setDataImportAvailable(data.data_import_available);
      } else if (tab === 'reports') {
        const data = await importLogsApi.getSyncReports();
        setSyncReports(data.items || []);
        setDataImportAvailable(data.data_import_available);
      } else if (tab === 'files') {
        const data = await importLogsApi.getFileLogs();
        setFileLogs(data.items || []);
        setDataImportAvailable(data.data_import_available);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки логов');
    }
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    setExpandedLogId(null);
    setLogChanges(null);
    setExpandedCompareId(null);
    setCompareDetail(null);
    setExpandedReport(null);
    setReportDetail(null);
    setSelectedFileLog(null);
    setFileLogContent(null);
    await loadTabData(tab);
  };

  const toggleLogChanges = async (logId) => {
    if (expandedLogId === logId) {
      setExpandedLogId(null);
      setLogChanges(null);
      return;
    }
    setExpandedLogId(logId);
    try {
      const detail = await importLogsApi.getImportChanges(logId);
      setLogChanges(detail);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось загрузить изменения');
    }
  };

  const toggleCompare = async (reportId) => {
    if (expandedCompareId === reportId) {
      setExpandedCompareId(null);
      setCompareDetail(null);
      return;
    }
    setExpandedCompareId(reportId);
    try {
      const detail = await importLogsApi.getCompareReport(reportId);
      setCompareDetail(detail);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось загрузить отчёт сравнения');
    }
  };

  const toggleReport = async (runId) => {
    if (expandedReport === runId) {
      setExpandedReport(null);
      setReportDetail(null);
      return;
    }
    setExpandedReport(runId);
    try {
      const detail = await importLogsApi.getSyncReport(runId);
      setReportDetail(detail);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось загрузить отчёт');
    }
  };

  const openFileLog = async (filename) => {
    setSelectedFileLog(filename);
    try {
      const content = await importLogsApi.getFileLog(filename);
      setFileLogContent(content);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось загрузить файл лога');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Загрузка...</div>
      </div>
    );
  }

  if (!currentUser?.is_admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FiFileText className="mr-2" />
            Логи
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Входы пользователей, импорты, сравнения с 1С
          </p>
        </div>
        <button
          onClick={() => loadTabData(activeTab)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <FiRefreshCw className="mr-2" />
          Обновить
        </button>
      </div>

      {!dataImportAvailable && ['compare', 'reports', 'files'].includes(activeTab) && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          Каталог <code className="bg-yellow-100 px-1 rounded">data_import</code> недоступен в контейнере backend.
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm inline-flex items-center ${
                activeTab === id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="mr-2" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'sessions' && (
        <div className="space-y-4">
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm text-gray-600">
                С
                <input
                  type="date"
                  value={sessionFrom}
                  onChange={(e) => setSessionFrom(e.target.value)}
                  className="mt-1 block border rounded-lg px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm text-gray-600">
                По
                <input
                  type="date"
                  value={sessionTo}
                  onChange={(e) => setSessionTo(e.target.value)}
                  className="mt-1 block border rounded-lg px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm text-gray-600">
                Пользователь
                <select
                  value={sessionUserId}
                  onChange={(e) => setSessionUserId(e.target.value)}
                  className="mt-1 block border rounded-lg px-3 py-2 text-sm min-w-[220px]"
                >
                  <option value="">Все</option>
                  {sessionUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.full_name}</option>
                  ))}
                </select>
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 pb-2">
                <input
                  type="checkbox"
                  checked={sessionBookedOnly}
                  onChange={(e) => setSessionBookedOnly(e.target.checked)}
                />
                Только кто бронировал
              </label>
              <button
                type="button"
                onClick={() => loadTabData('sessions')}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-900"
              >
                Показать
              </button>
              <button
                type="button"
                disabled={exporting}
                onClick={async () => {
                  setExporting(true);
                  try {
                    const blob = await importLogsApi.exportSessions({
                      date_from: sessionFrom || undefined,
                      date_to: sessionTo || undefined,
                      user_id: sessionUserId || undefined,
                      booked_only: sessionBookedOnly || undefined,
                    });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `vhodi_${new Date().toISOString().slice(0, 10)}.xlsx`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    setError(err.response?.data?.detail || 'Не удалось выгрузить Excel');
                  } finally {
                    setExporting(false);
                  }
                }}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
              >
                <FiDownload className="mr-2" />
                {exporting ? 'Выгрузка…' : 'Экспорт в Excel'}
              </button>
            </div>
            {sessionStats && (
              <div className="text-xs text-gray-500 mt-3 flex flex-wrap gap-4">
                <span>сессий: {sessionStats.sessions}</span>
                <span>уникальных пользователей: {sessionStats.unique_users}</span>
                <span>с бронью: {sessionStats.with_booking}</span>
              </div>
            )}
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            {sessionRows.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Записей пока нет — лог ведётся с момента обновления системы
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Пользователь</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Вход</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Выход / активность</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Сколько сидел</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Резерв</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Брони</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {sessionRows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{row.full_name}</td>
                        <td className="px-3 py-2 text-gray-600">{row.email}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.login_at)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {row.is_active ? (
                            <span className="text-green-700">сейчас (активен)</span>
                          ) : (
                            formatDate(row.ended_at)
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{row.duration_label}</td>
                        <td className="px-3 py-2">
                          {row.booked ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">да</span>
                          ) : (
                            <span className="text-gray-400">нет</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">
                          {row.bookings?.length
                            ? row.bookings.map((b) => `#${b.id}`).join(', ')
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'db' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {dbLogs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Записей пока нет</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {dbLogs.map((log) => (
                <div key={log.id}>
                  <button
                    type="button"
                    onClick={() => toggleLogChanges(log.id)}
                    className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{formatDate(log.imported_at)}</span>
                        {statusBadge(log.status)}
                        <span className="text-sm text-gray-600 truncate" title={log.filename}>
                          {basename(log.filename)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-3">
                        <span>новые: {log.summary?.imported ?? '—'}</span>
                        <span>обновл.: {log.summary?.updated ?? '—'}</span>
                        <span>без изм.: {log.summary?.unchanged ?? '—'}</span>
                        <span>продано: {log.summary?.sold_count ?? '—'}</span>
                        {(log.summary?.changes_count ?? 0) > 0 && (
                          <span className="text-primary-600 font-medium">
                            изменений: {log.summary.changes_count}
                          </span>
                        )}
                      </div>
                    </div>
                    {expandedLogId === log.id ? <FiChevronUp /> : <FiChevronDown />}
                  </button>
                  {expandedLogId === log.id && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      {logChanges?.import_log_id === log.id ? (
                        <>
                          <p className="text-xs text-gray-500 mb-3">
                            Детализация изменений при импорте. Поля «кому продано» появятся после интеграции с 1С.
                          </p>
                          <ChangesTable
                            items={logChanges.items}
                            emptyText="Детализация для этого импорта не сохранена (старые записи до обновления системы)"
                          />
                        </>
                      ) : (
                        <div className="text-sm text-gray-500">Загрузка...</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'compare' && (
        <div className="space-y-3">
          {compareReports.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
              Отчётов сравнения в data_import/runs/ пока нет
            </div>
          ) : (
            compareReports.map((report) => (
              <div key={report.report_id} className="bg-white shadow rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleCompare(report.report_id)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
                >
                  <div>
                    <div className="font-medium text-gray-900 text-sm">
                      {report.source_file || report.report_id}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {formatDate(report.modified_at)}
                      {report.summary && (
                        <span className="ml-2">
                          · новых {report.summary.new ?? 0}
                          · изм. {report.summary.updated_qty_or_revived ?? 0}
                          · без изм. {report.summary.unchanged ?? 0}
                        </span>
                      )}
                    </div>
                  </div>
                  {expandedCompareId === report.report_id ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                {expandedCompareId === report.report_id && compareDetail && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <CompareChangesTable report={compareDetail} />
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="space-y-3">
          {syncReports.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
              Отчётов в data_import/runs/ пока нет
            </div>
          ) : (
            syncReports.map((report) => (
              <div key={report.run_id} className="bg-white shadow rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleReport(report.run_id)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
                >
                  <div>
                    <div className="font-medium text-gray-900 text-sm">{report.run_id}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {report.created_at || formatDate(report.modified_at)} · {report.mode || '—'} · {statusBadge(report.status)}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    {report.diff && (
                      <span>
                        new {report.diff.new_count ?? '—'} · upd {report.diff.updated_count ?? '—'} · sold {report.diff.sold_count ?? '—'}
                      </span>
                    )}
                    {expandedReport === report.run_id ? <FiChevronUp /> : <FiChevronDown />}
                  </div>
                </button>
                {expandedReport === report.run_id && reportDetail && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <pre className="text-xs overflow-x-auto whitespace-pre-wrap text-gray-800">
                      {JSON.stringify(reportDetail, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'files' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 bg-white shadow rounded-lg overflow-hidden">
            {fileLogs.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">Логов в data_import/logs/ пока нет</div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {fileLogs.map((item) => (
                  <li key={item.filename}>
                    <button
                      onClick={() => openFileLog(item.filename)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${
                        selectedFileLog === item.filename ? 'bg-primary-50' : ''
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900 truncate">{item.filename}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatDate(item.modified_at)} · {formatBytes(item.size_bytes)}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="lg:col-span-2 bg-white shadow rounded-lg p-4 min-h-[320px]">
            {!fileLogContent ? (
              <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                Выберите файл слева
              </div>
            ) : (
              <div>
                <div className="text-sm font-medium text-gray-900 mb-2">
                  {fileLogContent.filename}
                  <span className="text-gray-500 font-normal ml-2">
                    (последние {fileLogContent.tail_lines} из {fileLogContent.total_lines} строк)
                  </span>
                </div>
                <pre className="text-xs bg-gray-900 text-green-200 p-4 rounded-lg overflow-auto max-h-[600px] whitespace-pre-wrap">
                  {fileLogContent.content}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Logs;
